import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { motion } from "framer-motion";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role) return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : u.onboarded ? "/home" : "/onboarding");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div className="bps-ambient" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <img
            src="/fbp-logo.png"
            alt="Freedom From Back Pain"
            data-testid="bps-logo"
            className="mx-auto w-44 sm:w-56 h-auto mb-2 drop-shadow-[0_0_24px_rgba(34,211,238,0.35)] select-none"
            draggable={false}
          />
          <div className="bps-eyebrow">Back Pain Solution</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter mt-3 text-white">
            Reset Mind.<br/>Reprogrammed Body.
          </h1>
          <p className="text-sm text-[#9AAEC6] mt-4">Welcome back. Step inside your reset.</p>
        </div>

        <form onSubmit={submit} className="space-y-4 bps-card p-6" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#7C8FA8]">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="login-email"
              className="w-full bg-white/5 rounded-2xl px-4 py-4 mt-1 outline-none focus:ring-2 focus:ring-[#22D3EE] transition-all"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#7C8FA8]">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="login-password"
              className="w-full bg-white/5 rounded-2xl px-4 py-4 mt-1 outline-none focus:ring-2 focus:ring-[#22D3EE] transition-all"
            />
          </div>
          {err && <div className="text-sm text-[#F87171]" data-testid="login-error">{err}</div>}
          <button
            disabled={loading}
            data-testid="login-submit"
            className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center pt-1">
            <Link
              to="/forgot-password"
              className="text-xs text-[#9AAEC6] hover:text-[#22D3EE] transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
          </div>
        </form>

        <p className="text-center text-sm text-[#9AAEC6] mt-6">
          New here?{" "}
          <Link to="/register" className="text-[#22D3EE] font-medium" data-testid="goto-register">
            Create your account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
